<template>
  <div class="lb">
    <div class="lb__card">
      <header class="lb__header">
        <i class="bi bi-trophy-fill lb__header-icon" aria-hidden="true" />
        <div>
          <h2 class="lb__title">جدول امتیازات</h2>
          <p class="lb__subtitle">هفتگی · XP</p>
        </div>
      </header>

      <div class="lb__your-rank">
        <div class="lb__your-rank-main">
          <span class="lb__your-rank-label">رتبه شما</span>
          <span class="lb__your-rank-value">{{ yourRankLabel }}</span>
        </div>
        <div class="lb__your-rank-meta">
          <span class="lb__your-xp">{{ yourXp }} XP</span>
          <span v-if="yourStreak > 0" class="lb__your-streak" title="روز متوالی">
            <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
              <path d="M12 2s4 4.2 4 8a4 4 0 1 1-8 0c0-2.4 1.4-4.6 2.6-6.2.4-.5 1.4.1 1.2.8C11.4 6.2 12 8 12 9c0-3-1.5-5.5-3-7 3 1 5 4 5 7a5 5 0 1 1-10 0c0-3.5 2.5-6.5 5-9z" />
            </svg>
            {{ yourStreak }}
          </span>
        </div>
      </div>

      <LoadingSpinner v-if="loading" message="در حال بارگذاری..." />

      <ErrorMessage
        v-else-if="errorMessage"
        :message="errorMessage"
        @retry="load"
      />

      <EmptyState
        v-else-if="!displayRows.length"
        title="هنوز امتیازی ثبت نشده"
        message="با تکمیل آزمون، XP هفتگی‌ات اینجا دیده می‌شود."
      />

      <template v-else>
        <div v-if="podiumOrder.length" class="lb__podium">
          <div
            v-for="entry in podiumOrder"
            :key="entry.user_id + '-' + entry.rank"
            class="lb__podium-card"
            :class="`lb__podium-card--rank-${entry.rank}`"
          >
            <div class="lb__medal" :class="`lb__medal--rank-${entry.rank}`">
              <svg
                class="lb__medal-svg"
                viewBox="0 0 56 64"
                fill="none"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient
                    :id="`medal-gradient-${entry.rank}`"
                    x1="8" y1="4" x2="48" y2="60"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0%"   :stop-color="medalGradient(entry.rank).start" />
                    <stop offset="55%"  :stop-color="medalGradient(entry.rank).mid" />
                    <stop offset="100%" :stop-color="medalGradient(entry.rank).end" />
                  </linearGradient>
                </defs>
                <path :fill="`url(#medal-gradient-${entry.rank})`" d="M20.2 8.2C14.8 9.8 12.2 15.8 14.2 21.6C15.2 24.4 17.6 25.8 19.8 24.8L20.8 21.2C19.2 18.6 18.8 14.2 20.8 10.8C20.6 9.8 20.4 9 20.2 8.2Z" />
                <path :fill="`url(#medal-gradient-${entry.rank})`" d="M35.8 8.2C41.2 9.8 43.8 15.8 41.8 21.6C40.8 24.4 38.4 25.8 36.2 24.8L35.2 21.2C36.8 18.6 37.2 14.2 35.2 10.8C35.4 9.8 35.6 9 35.8 8.2Z" />
                <circle cx="28" cy="33" r="16" :fill="`url(#medal-gradient-${entry.rank})`" />
                <rect x="25" y="48" width="6" height="5.5" rx="1.5" :fill="`url(#medal-gradient-${entry.rank})`" />
                <rect x="18.5" y="53.5" width="19" height="5" rx="2.5" :fill="`url(#medal-gradient-${entry.rank})`" />
                <text x="28" y="38.5" text-anchor="middle" class="lb__medal-label">{{ entry.rank }}</text>
              </svg>
            </div>
            <p class="lb__podium-name">{{ entry.name }}</p>
            <p class="lb__podium-score" :class="{ 'lb__podium-score--gold': entry.rank === 1 }">
              {{ entry.score }} XP
            </p>
            <p v-if="entry.streak > 0" class="lb__podium-streak">
              {{ entry.streak }} روز متوالی
            </p>
          </div>
        </div>

        <div v-if="listRows.length" class="lb__list">
          <div
            v-for="row in listRows"
            :key="row.user_id + '-' + row.rank"
            class="lb__row"
            :class="{ 'lb__row--me': row.isMe }"
          >
            <span class="lb__row-rank">{{ row.rank }}</span>
            <div
              class="lb__avatar"
              :style="{ background: avatarStyle(row).bg, color: avatarStyle(row).color }"
            >
              {{ initials(row.name) }}
            </div>
            <span class="lb__row-name">{{ row.name }}</span>
            <span v-if="row.streak > 0" class="lb__row-streak" title="روز متوالی">
              <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor" aria-hidden="true">
                <path d="M12 2s4 4.2 4 8a4 4 0 1 1-8 0c0-2.4 1.4-4.6 2.6-6.2.4-.5 1.4.1 1.2.8C11.4 6.2 12 8 12 9c0-3-1.5-5.5-3-7 3 1 5 4 5 7a5 5 0 1 1-10 0c0-3.5 2.5-6.5 5-9z" />
              </svg>
              {{ row.streak }}
            </span>
            <span class="lb__row-xp">{{ row.score }} XP</span>
          </div>
        </div>

        <button type="button" class="lb__footer-btn" disabled>
          <i class="bi bi-trophy" aria-hidden="true" />
          مشاهده جدول کامل
        </button>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { getWeeklyLeaderboard } from "@/api/leaderboard";
import { useAuth } from "@/composables/useAuth";
import { handleError } from "@/services/errorHandler";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import ErrorMessage from "@/components/common/ErrorMessage.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import type { WeeklyLeaderboardRow } from "@/types";

const AVATAR_COLORS = [
  { bg: "#f4a97a", color: "#ffffff" },
  { bg: "#6ee7b7", color: "#ffffff" },
  { bg: "#86efac", color: "#ffffff" },
  { bg: "#fca5a5", color: "#ffffff" },
  { bg: "#93c5fd", color: "#ffffff" },
  { bg: "#f9a8d4", color: "#ffffff" },
  { bg: "#c4b5fd", color: "#ffffff" },
];

interface DisplayRow {
  rank: number;
  user_id: number;
  name: string;
  score: number;
  streak: number;
  isMe: boolean;
}

export default {
  name: "leaderBord",
  components: { LoadingSpinner, ErrorMessage, EmptyState },
  data() {
    return {
      rows: [] as WeeklyLeaderboardRow[],
      loading: true,
      errorMessage: null as string | null,
      yourRank: null as number | null,
      yourXp: 0,
      yourStreak: 0,
    };
  },
  computed: {
    currentUserId(): number | null {
      const { user } = useAuth();
      return typeof user.value?.id === "number" ? user.value.id : null;
    },
    yourRankLabel(): string {
      if (this.yourRank == null) return "—";
      return `#${this.yourRank}`;
    },
    displayRows(): DisplayRow[] {
      return this.rows.slice(0, 10).map((row) => ({
        rank: row.rank,
        user_id: row.user_id,
        name: row.username || `کاربر #${row.user_id}`,
        score: row.xp,
        streak: row.current_streak || 0,
        isMe: this.currentUserId != null && row.user_id === this.currentUserId,
      }));
    },
    podiumOrder(): DisplayRow[] {
      const top = this.displayRows.slice(0, 3);
      if (top.length === 0) return [];
      if (top.length === 1) return top;
      if (top.length === 2) return [top[1], top[0]];
      return [top[1], top[0], top[2]];
    },
    listRows(): DisplayRow[] {
      return this.displayRows.slice(3, 10);
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.errorMessage = null;
      try {
        const data = await getWeeklyLeaderboard({ limit: 10 });
        this.rows = data.items || [];
        this.yourRank = data.your_rank;
        this.yourXp = data.your_xp || 0;
        this.yourStreak = data.your_streak || 0;
      } catch (e) {
        this.errorMessage = handleError(e, "leaderBord.load");
        this.rows = [];
        this.yourRank = null;
        this.yourXp = 0;
        this.yourStreak = 0;
      } finally {
        this.loading = false;
      }
    },
    avatarStyle(row: DisplayRow) {
      const index = Number(row.user_id ?? row.rank) % AVATAR_COLORS.length;
      return AVATAR_COLORS[index];
    },
    initials(name: string) {
      return name
        .split(" ")
        .map((p) => p[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },
    medalGradient(rank: number) {
      if (rank === 1) return { start: "#ffe566", mid: "#f5c518", end: "#c99212" };
      if (rank === 2) return { start: "#f0f4fa", mid: "#c5ccd8", end: "#8b95a5" };
      return { start: "#f0a96a", mid: "#d9793f", end: "#a85a28" };
    },
  },
};
</script>

<style scoped>
.lb {
  width: 100%;
  direction: rtl;
  font-family: Vazirmatn, system-ui, "Segoe UI", Tahoma, sans-serif;
}

.lb__card {
  background: #ffffff;
  border: 1px solid #ECE4D9;
  border-radius: 24px;
  padding: 22px 20px 18px;
  box-shadow: 0 4px 24px rgba(76, 55, 42, 0.07);
  color: #4C372A;
}

.lb__header {
  display: flex;
  align-items: flex-start;
  flex-direction: row;
  gap: 10px;
  margin-bottom: 16px;
}

.lb__header-icon {
  font-size: 1.3rem;
  color: #865FEA;
  margin-top: 2px;
}

.lb__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #4C372A;
  line-height: 1.2;
}

.lb__subtitle {
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: #8C8176;
}

.lb__your-rank {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 18px;
  background: #FBF7FC;
  border: 1px solid #ECE4D9;
  border-radius: 12px;
}

.lb__your-rank-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lb__your-rank-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4C372A;
}

.lb__your-rank-value {
  font-size: 1rem;
  font-weight: 700;
  color: #6D5EF8;
}

.lb__your-rank-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.lb__your-xp {
  font-size: 0.85rem;
  font-weight: 700;
  color: #E8A317;
}

.lb__your-streak {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #F59E0B;
}

.lb__podium {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
}

.lb__podium-card {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 14px 8px 12px;
  background: #FFFDF8;
  border: 1px solid #ECE4D9;
  border-radius: 16px;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.lb__podium-card--rank-1 {
  min-height: 124px;
  padding-top: 20px;
  background: #FBF7FC;
  border-color: #c4b5fd;
}

.lb__podium-card--rank-2,
.lb__podium-card--rank-3 {
  min-height: 104px;
}

.lb__podium-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(109, 94, 248, 0.1);
}

.lb__medal {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.lb__medal-svg {
  width: 46px;
  height: 52px;
  display: block;
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.15));
}

.lb__medal--rank-1 .lb__medal-svg { width: 54px; height: 62px; }
.lb__medal--rank-2 .lb__medal-svg,
.lb__medal--rank-3 .lb__medal-svg { width: 44px; height: 50px; }

.lb__medal-label {
  fill: #ffffff;
  font-size: 17px;
  font-weight: 800;
  font-family: Vazirmatn, system-ui, "Segoe UI", Tahoma, sans-serif;
  paint-order: stroke fill;
}

.lb__medal--rank-1 .lb__medal-label { font-size: 19px; }

.lb__podium-name {
  margin: 0 0 4px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #4C372A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.lb__podium-score {
  margin: 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6D5EF8;
}

.lb__podium-score--gold {
  color: #f5c518;
}

.lb__podium-streak {
  margin: 4px 0 0;
  font-size: 0.68rem;
  font-weight: 600;
  color: #F59E0B;
}

.lb__list {
  background: #FFFDF8;
  border: 1px solid #ECE4D9;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 14px;
}

.lb__row {
  display: grid;
  grid-template-columns: 20px 36px 1fr auto auto;
  align-items: center;
  gap: 8px;
  padding: 11px 14px;
  border-bottom: 1px solid #ECE4D9;
  transition: background-color 0.18s ease;
}

.lb__row:last-child { border-bottom: none; }

.lb__row:hover,
.lb__row--me { background: #FBF7FC; }

.lb__row-rank {
  font-size: 0.85rem;
  font-weight: 600;
  color: #8C8176;
  text-align: center;
}

.lb__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
}

.lb__row-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #4C372A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.lb__row-streak {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #F59E0B;
  white-space: nowrap;
}

.lb__row-xp {
  font-size: 0.78rem;
  font-weight: 500;
  color: #8C8176;
  white-space: nowrap;
}

.lb__footer-btn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border: 1px solid #ECE4D9;
  border-radius: 12px;
  background: #FFFDF8;
  color: #4C372A;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: not-allowed;
  opacity: 0.7;
  font-family: Vazirmatn, system-ui, "Segoe UI", Tahoma, sans-serif;
}

.lb__footer-btn .bi { font-size: 0.9rem; color: #865FEA; }
</style>
